import React from 'react';
import styled from 'styled-components';
import SanityImage from 'gatsby-plugin-sanity-image';
import PropTypes from 'prop-types';
import { LocalizedLink } from '@rsc/gatsby-localization-helpers';
import breakpoints from '../../breakpoints';

const Container = styled.div`
  h2 {
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 2rem;
    font-size: 2.25rem;
  }

  margin-bottom: 2rem;
  @media (min-width: ${breakpoints.md}) {
    margin-bottom: 3rem;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
  @media (min-width: ${breakpoints.md}) {
    gap: 1.25rem;
    flex-direction: row;
  }
`;

const Card = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  background: ${({ theme }) => theme.colors.cardBackground};

  a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.defaultFont};

    :hover {
      color: ${({ theme }) => theme.colors.brandPrimary};
    }
  }
`;

const CardText = styled.div`
  margin: 0;
  padding: 0.75rem 0.25rem;
  text-align: center;
  text-transform: uppercase;
  font-size: 1.25rem;
  @media (min-width: ${breakpoints.md}) {
    font-size: 0.875rem;
  }
  @media (min-width: ${breakpoints.lg}) {
    font-size: 1.125rem;
  }
`;

const ImageContainer = styled.div`
  aspect-ratio: 16/13;
  overflow: hidden;
  @media (min-width: ${breakpoints.lg}) {
    aspect-ratio: 1/1;
  }

  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

function RowItem({ item }) {
  return (
    <>
      {item?.media?.asset && (
        <ImageContainer>
          <SanityImage
            asset={item?.media?.asset}
            hotspot={item?.media?.hotspot}
            alt={item?.media?.alt || item.heading}
            width={400}
          />
        </ImageContainer>
      )}

      {item?.heading && <CardText>{item.heading}</CardText>}
    </>
  );
}

function CardRow({ value: { items, heading }, getInternalLink }) {
  return (
    <Container>
      <h2>{heading}</h2>
      {items?.length && (
        <CardsContainer>
          {items?.map(item => (
            // eslint-disable-next-line no-underscore-dangle
            <Card key={item?._key}>
              {item?.link?.linkType === 'external' ? (
                <a
                  href={item?.link?.href}
                  target={item?.link?.blank ? '_blank' : '_self'}
                  rel="noreferrer"
                >
                  <RowItem item={item} />
                </a>
              ) : (
                <LocalizedLink
                  to={getInternalLink({
                    ...item.link,
                    reference: item?.link?.internalLink,
                  })}
                >
                  <RowItem item={item} />
                </LocalizedLink>
              )}
            </Card>
          ))}
        </CardsContainer>
      )}
    </Container>
  );
}

RowItem.propTypes = {
  item: PropTypes.shape({
    heading: PropTypes.string,
    media: PropTypes.shape({
      alt: PropTypes.string,
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
      hotspot: PropTypes.shape({}),
    }),
  }).isRequired,
};

CardRow.propTypes = {
  value: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        heading: PropTypes.string,
        media: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
          hotspot: PropTypes.shape({}),
        }),
      })
    ),
    heading: PropTypes.string,
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default CardRow;
